import { getAuthedFetch, type AuthedFetch } from "./auth";

export class CloudApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    message = `Cloud API request failed with ${status}`,
    readonly retryAfterSeconds: number | null = null
  ) {
    super(message);
    this.name = "CloudApiError";
  }
}

export type CloudProject = {
  id: string;
  ownerPrincipalId: string;
  name: string;
  thumbnailHash: string | null;
  createdAt: number;
  updatedAt: number;
  trashedAt: number | null;
  autosaveOnly?: boolean;
  autosaveVersionCount?: number;
  manualVersionCount?: number;
  isOwner?: boolean;
  accessRole?: "owner" | "editor" | "viewer" | string;
};

export type CloudProjectShare = {
  projectId: string;
  principalId: string;
  role: "viewer" | "editor" | string;
  grantedAt: number;
  expiresAt: number | null;
};

export type CloudProjectVersion = {
  id: string;
  projectId: string;
  versionNumber: number;
  kind: "autosave" | "named" | "milestone" | string;
  label: string | null;
  metadataSize: number;
  createdAt: number;
  squashedAt: number | null;
};

export type VersionAssetOrigin = "library" | "principal";

export type PreflightAsset = {
  hash: string;
  size: number;
  contentType?: string | null;
};

export type PreflightResponse = {
  needsUpload: Array<{
    hash: string;
    uploadUrl: string;
    method: "PUT";
    headers: Record<string, string>;
    expiresAt: number;
  }>;
  alreadyStored: Array<{ hash: string; origin: VersionAssetOrigin }>;
};

export type VersionDetailResponse = {
  version: CloudProjectVersion;
  bundleUrl: string;
  assets: Record<string, { url: string; origin: VersionAssetOrigin }>;
};

export type CloudVersionShareLink = {
  id: string;
  projectId: string;
  versionId: string;
  kind: "share" | "embed" | string;
  path?: string;
  role: "viewer" | "editor" | string;
  createdAt: number;
  expiresAt: number | null;
  revokedAt: number | null;
  lastUsedAt: number | null;
};

export type CreatedCloudVersionShareLink = CloudVersionShareLink & {
  token: string;
  path: string;
};

export type PublicShareLinkResponse = {
  shareLink: CloudVersionShareLink;
  project: CloudProject;
  version: CloudProjectVersion;
  accessRole: "viewer" | "editor";
  readOnly: boolean;
};

export type PublicShareVersionDetailResponse = VersionDetailResponse & {
  shareLink: CloudVersionShareLink;
  accessRole: "viewer" | "editor";
  readOnly: boolean;
};

export type PublicEmbedResponse = PublicShareLinkResponse;

export type PublicEmbedVersionDetailResponse = PublicShareVersionDetailResponse;

export type UserLibraryItemType = "robot" | "attachment" | "object" | "mesh" | "scene";

export type UserLibraryItem = {
  id: string;
  ownerPrincipalId: string;
  type: UserLibraryItemType | string;
  name: string;
  description: string | null;
  thumbnailHash: string | null;
  sourceProjectId: string | null;
  sourceVersionId: string | null;
  metadata: Record<string, unknown>;
  createdAt: number;
  updatedAt: number;
  trashedAt: number | null;
};

type ApiOptions = {
  baseUrl?: string;
  fetchImpl?: AuthedFetch;
  shareToken?: string | null;
};

type PublicApiOptions = {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
};

function apiBaseUrl(baseUrl?: string): string {
  const env = import.meta.env ?? {};
  return baseUrl ?? env.VITE_CLOUD_API_BASE_URL ?? "";
}

function apiUrl(path: string, baseUrl?: string): string {
  return `${apiBaseUrl(baseUrl)}${path}`;
}

async function parseJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function parseRetryAfterSeconds(response: Response): number | null {
  const value = response.headers.get("retry-after");
  if (!value) return null;

  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds;

  const retryAt = Date.parse(value);
  if (!Number.isFinite(retryAt)) return null;
  return Math.max(0, Math.ceil((retryAt - Date.now()) / 1000));
}

async function request<T>(path: string, init: RequestInit, options?: ApiOptions): Promise<T> {
  const fetchImpl = options?.fetchImpl ?? getAuthedFetch();
  const response = await fetchImpl(apiUrl(path, options?.baseUrl), {
    ...init,
    headers: {
      ...(init.body instanceof FormData ? {} : { "content-type": "application/json" }),
      ...(options?.shareToken ? { "x-critter-share-token": options.shareToken } : {}),
      ...init.headers,
    },
  });
  const body = await parseJson(response);
  if (!response.ok)
    throw new CloudApiError(response.status, body, undefined, parseRetryAfterSeconds(response));
  return body as T;
}

async function publicRequest<T>(
  path: string,
  init: RequestInit,
  options?: PublicApiOptions
): Promise<T> {
  const fetchImpl = options?.fetchImpl ?? fetch;
  const response = await fetchImpl(apiUrl(path, options?.baseUrl), {
    ...init,
    headers: {
      ...(init.body instanceof FormData ? {} : { "content-type": "application/json" }),
      ...init.headers,
    },
  });
  const body = await parseJson(response);
  if (!response.ok)
    throw new CloudApiError(response.status, body, undefined, parseRetryAfterSeconds(response));
  return body as T;
}

export function createProject(
  name: string,
  options?: ApiOptions
): Promise<{ projectId: string; project: CloudProject }> {
  return request("/api/projects", { method: "POST", body: JSON.stringify({ name }) }, options);
}

export function listProjects(
  params: { includeTrashed?: boolean; limit?: number; cursor?: string } = {},
  options?: ApiOptions
): Promise<{ projects: CloudProject[]; nextCursor: string | null }> {
  const search = new URLSearchParams();
  if (params.includeTrashed) search.set("includeTrashed", "true");
  if (params.limit) search.set("limit", String(params.limit));
  if (params.cursor) search.set("cursor", params.cursor);
  const suffix = search.toString() ? `?${search.toString()}` : "";
  return request(`/api/projects${suffix}`, { method: "GET" }, options);
}

export function getProject(
  projectId: string,
  options?: ApiOptions
): Promise<{ project: CloudProject; latestVersion: CloudProjectVersion | null }> {
  return request(`/api/projects/${encodeURIComponent(projectId)}`, { method: "GET" }, options);
}

export function patchProject(
  projectId: string,
  body: { name?: string; trashed?: boolean },
  options?: ApiOptions
): Promise<{ project: CloudProject }> {
  return request<{ project: CloudProject }>(
    `/api/projects/${encodeURIComponent(projectId)}`,
    { method: "PATCH", body: JSON.stringify(body) },
    options
  );
}

export function deleteProject(projectId: string, options?: ApiOptions): Promise<{ ok: true }> {
  return request(`/api/projects/${encodeURIComponent(projectId)}`, { method: "DELETE" }, options);
}

export function listProjectShares(
  projectId: string,
  options?: ApiOptions
): Promise<{ shares: CloudProjectShare[] }> {
  return request<{ shares: CloudProjectShare[] }>(
    `/api/projects/${encodeURIComponent(projectId)}/shares`,
    { method: "GET" },
    options
  );
}

export function shareProject(
  projectId: string,
  body: { principalId: string; role: "viewer" | "editor"; expiresAt?: number | null },
  options?: ApiOptions
): Promise<{ share: CloudProjectShare }> {
  return request(
    `/api/projects/${encodeURIComponent(projectId)}/shares`,
    { method: "POST", body: JSON.stringify(body) },
    options
  );
}

export function deleteProjectShare(
  projectId: string,
  principalId: string,
  options?: ApiOptions
): Promise<{ ok: true }> {
  return request(
    `/api/projects/${encodeURIComponent(projectId)}/shares/${encodeURIComponent(principalId)}`,
    { method: "DELETE" },
    options
  );
}

export function preflightVersion(
  projectId: string,
  hashes: PreflightAsset[],
  options?: ApiOptions
): Promise<PreflightResponse> {
  return request(
    `/api/projects/${encodeURIComponent(projectId)}/versions:preflight`,
    { method: "POST", body: JSON.stringify({ hashes }) },
    options
  );
}

export function commitVersion(
  projectId: string,
  args: {
    bundle: Blob;
    manifest: {
      kind: "autosave" | "named" | "milestone";
      label?: string | null;
      baseVersionId?: string | null;
      thumbnailHash?: string | null;
      assets: Array<{ hash: string }>;
    };
  },
  options?: ApiOptions
): Promise<{ versionId: string; versionNumber: number }> {
  const form = new FormData();
  form.set("bundle", args.bundle);
  form.set("manifest", JSON.stringify(args.manifest));
  return request(
    `/api/projects/${encodeURIComponent(projectId)}/versions`,
    { method: "POST", body: form },
    options
  );
}

export function listVersions(
  projectId: string,
  options?: ApiOptions
): Promise<{ versions: CloudProjectVersion[] }> {
  return request(
    `/api/projects/${encodeURIComponent(projectId)}/versions`,
    { method: "GET" },
    options
  );
}

export function getVersionDetail(
  projectId: string,
  versionId: string,
  options?: ApiOptions
): Promise<VersionDetailResponse> {
  return request(
    `/api/projects/${encodeURIComponent(projectId)}/versions/${encodeURIComponent(versionId)}`,
    { method: "GET" },
    options
  );
}

export function deleteVersion(
  projectId: string,
  versionId: string,
  options?: ApiOptions
): Promise<{ ok: true }> {
  return request(
    `/api/projects/${encodeURIComponent(projectId)}/versions/${encodeURIComponent(versionId)}`,
    { method: "DELETE" },
    options
  );
}

export function labelVersion(
  projectId: string,
  versionId: string,
  label: string,
  options?: ApiOptions
): Promise<{ version: CloudProjectVersion }> {
  return request(
    `/api/projects/${encodeURIComponent(projectId)}/versions/${encodeURIComponent(versionId)}:label`,
    { method: "POST", body: JSON.stringify({ label }) },
    options
  );
}

export function listVersionShareLinks(
  projectId: string,
  versionId: string,
  params: { kind?: "share" | "embed" } = {},
  options?: ApiOptions
): Promise<{ shareLinks: CloudVersionShareLink[] }> {
  const search = new URLSearchParams();
  if (params.kind) search.set("kind", params.kind);
  const suffix = search.toString() ? `?${search.toString()}` : "";
  return request(
    `/api/projects/${encodeURIComponent(projectId)}/versions/${encodeURIComponent(
      versionId
    )}/share-links${suffix}`,
    { method: "GET" },
    options
  );
}

export function createVersionShareLink(
  projectId: string,
  versionId: string,
  body: {
    expiresAt?: number | null;
    kind?: "share" | "embed";
    role?: "viewer" | "editor";
  } = {},
  options?: ApiOptions
): Promise<{ shareLink: CreatedCloudVersionShareLink }> {
  return request<{ shareLink: CreatedCloudVersionShareLink }>(
    `/api/projects/${encodeURIComponent(projectId)}/versions/${encodeURIComponent(
      versionId
    )}/share-links`,
    { method: "POST", body: JSON.stringify(body) },
    options
  );
}

export function listVersionEmbedLinks(
  projectId: string,
  versionId: string,
  options?: ApiOptions
): Promise<{ shareLinks: CloudVersionShareLink[] }> {
  return listVersionShareLinks(projectId, versionId, { kind: "embed" }, options);
}

export function createVersionEmbedLink(
  projectId: string,
  versionId: string,
  body: { expiresAt?: number | null } = {},
  options?: ApiOptions
): Promise<{ shareLink: CreatedCloudVersionShareLink }> {
  return request<{ shareLink: CreatedCloudVersionShareLink }>(
    `/api/projects/${encodeURIComponent(projectId)}/versions/${encodeURIComponent(
      versionId
    )}/embed-links`,
    { method: "POST", body: JSON.stringify(body) },
    options
  ).catch((error) => {
    if (error instanceof CloudApiError && error.status === 404) {
      throw new Error(
        "Embed links require the updated cloud API. Deploy the worker and D1 migration, or run the local worker API for local testing."
      );
    }
    throw error;
  });
}

export function revokeVersionShareLink(
  projectId: string,
  versionId: string,
  linkId: string,
  options?: ApiOptions
): Promise<{ ok: true }> {
  return request(
    `/api/projects/${encodeURIComponent(projectId)}/versions/${encodeURIComponent(
      versionId
    )}/share-links/${encodeURIComponent(linkId)}`,
    { method: "DELETE" },
    options
  );
}

export function getPublicShareLink(
  token: string,
  options?: PublicApiOptions
): Promise<PublicShareLinkResponse> {
  return publicRequest(`/api/share-links/${encodeURIComponent(token)}`, { method: "GET" }, options);
}

export function getPublicShareLinkVersionDetail(
  token: string,
  options?: PublicApiOptions
): Promise<PublicShareVersionDetailResponse> {
  return publicRequest(
    `/api/share-links/${encodeURIComponent(token)}/version`,
    { method: "GET" },
    options
  );
}

export function getPublicEmbed(
  token: string,
  options?: PublicApiOptions
): Promise<PublicEmbedResponse> {
  return publicRequest(`/api/embeds/${encodeURIComponent(token)}`, { method: "GET" }, options);
}

export function getPublicEmbedVersionDetail(
  token: string,
  options?: PublicApiOptions
): Promise<PublicEmbedVersionDetailResponse> {
  return publicRequest(
    `/api/embeds/${encodeURIComponent(token)}/version`,
    { method: "GET" },
    options
  );
}

export function getLibraryManifest(options?: ApiOptions): Promise<{
  assets: Record<
    string,
    { url: string; sourcePath: string; size: number; contentType: string | null }
  >;
}> {
  return request("/api/library/manifest", { method: "GET" }, options);
}

export function listUserLibraryItems(
  params: {
    type?: UserLibraryItemType;
    includeTrashed?: boolean;
    limit?: number;
    cursor?: string;
  } = {},
  options?: ApiOptions
): Promise<{ items: UserLibraryItem[]; nextCursor: string | null }> {
  const search = new URLSearchParams();
  if (params.type) search.set("type", params.type);
  if (params.includeTrashed) search.set("includeTrashed", "true");
  if (params.limit) search.set("limit", String(params.limit));
  if (params.cursor) search.set("cursor", params.cursor);
  const suffix = search.toString() ? `?${search.toString()}` : "";
  return request(`/api/user-library/items${suffix}`, { method: "GET" }, options);
}

export function createUserLibraryItem(
  body: {
    type: UserLibraryItemType;
    name: string;
    description?: string | null;
    metadata?: Record<string, unknown>;
    thumbnailHash?: string | null;
    sourceProjectId?: string | null;
    sourceVersionId?: string | null;
  },
  options?: ApiOptions
): Promise<{ item: UserLibraryItem }> {
  return request(
    "/api/user-library/items",
    { method: "POST", body: JSON.stringify(body) },
    options
  );
}

export function patchUserLibraryItem(
  itemId: string,
  body: {
    name?: string;
    description?: string | null;
    metadata?: Record<string, unknown>;
    thumbnailHash?: string | null;
    trashed?: boolean;
  },
  options?: ApiOptions
): Promise<{ item: UserLibraryItem }> {
  return request(
    `/api/user-library/items/${encodeURIComponent(itemId)}`,
    { method: "PATCH", body: JSON.stringify(body) },
    options
  );
}

export function deleteUserLibraryItem(itemId: string, options?: ApiOptions): Promise<{ ok: true }> {
  return request(
    `/api/user-library/items/${encodeURIComponent(itemId)}`,
    { method: "DELETE" },
    options
  );
}
